import sessionTimeout from '../utils/session-timeout';
import { logoutMerchant, getProfile } from '../api/auth';

export default {
  namespaced: true,
  state: {
    email: null,
    token: null,
    reset_password_token: null,
    profile: null,
    userRole: null,
    statusKyb: null,
    isAccountDemo: false,
    isSessionActive: false,
    showTimeoutWarning: false,
    timeoutWarningMessage: ''
  },
  mutations: {
    setEmail(state, email) {
      state.email = email;
    },
    setToken(state, token) {
      state.token = token;
    },
    setResetPasswordToken(state, reset_password_token) {
      state.reset_password_token = reset_password_token;
    },
    setProfile(state, profile) {
      state.profile = profile;
    },
    setUserRole(state, role) {
      state.userRole = role;
    },
    setStatusKyb(state, statusKyb) {
      state.statusKyb = statusKyb;
    },
    setIsAccountDemo(state, isAccountDemo) {
      state.isAccountDemo = isAccountDemo;
    },
    setSessionActive(state, isActive) {
      state.isSessionActive = isActive;
    },
    setTimeoutWarning(state, payload) {
      state.showTimeoutWarning = payload.show;
      state.timeoutWarningMessage = payload.message || '';
    },
    clearAuth(state) {
      state.email = null;
      state.token = null;
      state.profile = null;
      state.userRole = null;
      state.statusKyb = null;
      state.isAccountDemo = false;
      state.isSessionActive = false;
      state.showTimeoutWarning = false;
      state.timeoutWarningMessage = '';
    }
  },
  actions: {
    startSessionTimeout({ commit, dispatch }) {
      console.log('Starting session timeout...');
      
      const warningCallback = () => {
        const remainingTime = sessionTimeout.getRemainingTime();
        const message = `Your session will expire in ${remainingTime} minute(s). Do you want to extend your session?`;
        
        commit('setTimeoutWarning', {
          show: true,
          message: message
        });
      };

      const logoutCallback = () => {
        console.log('Session expired - logging out');
        dispatch('handleSessionExpiry');
      };

      sessionTimeout.start(warningCallback, logoutCallback);
      commit('setSessionActive', true);
    },

    stopSessionTimeout({ commit }) {
      console.log('Stopping session timeout...');
      sessionTimeout.stop();
      commit('setSessionActive', false);
      commit('setTimeoutWarning', { show: false, message: '' });
    },

    extendSession({ commit }) {
      console.log('Extending session...');
      sessionTimeout.extendSession();
      commit('setTimeoutWarning', { show: false, message: '' });
    },

    dismissTimeoutWarning({ commit }) {
      commit('setTimeoutWarning', { show: false, message: '' });
    },

    async handleSessionExpiry({ dispatch }) {
      // Stop the timeout first
      await dispatch('stopSessionTimeout');
      // Then logout
      await dispatch('logout');
    },

    async logout({ commit, dispatch }) {
      try {
        // Stop session timeout first
        await dispatch('stopSessionTimeout');
        
        // Call logout API to invalidate token on server
        try {
          await logoutMerchant();
          console.log('Server logout successful');
        } catch (apiError) {
          console.warn('Server logout failed, continuing with local logout:', apiError);
          // Continue with local logout even if API fails
        }
        
        // Clear all auth data
        commit('clearAuth');
        
        // Clear localStorage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('merchantEmail');
        localStorage.removeItem('merchantId');
        localStorage.removeItem('merchantKyc');
        localStorage.removeItem('merchantName');
        localStorage.removeItem('merchant_email');
        localStorage.removeItem('merchantAppId');
        localStorage.removeItem('merchantAppSecret');
        localStorage.removeItem('merchantStatusKyb');
        localStorage.removeItem('role');
        
        console.log('User logged out successfully');
        
        // Return success for components to handle navigation
        return { success: true };
      } catch (error) {
        console.error('Error during logout:', error);
        return { success: false, error };
      }
    },

    async setUserToken({ commit, dispatch }, token) {
      commit('setToken', token);
      localStorage.setItem('auth_token', token);
      
      // Start session timeout when token is set
      await dispatch('startSessionTimeout');
    },

    clearToken({ dispatch }) {
      dispatch('logout');
    },

    /**
     * Fetch user profile from API and update store with role and statusKyb
     * This is more secure than using localStorage which can be manipulated
     */
    async fetchUserProfile({ commit }) {
      try {
        const response = await getProfile();
        if (response?.data?.merchant) {
          const merchant = response.data.merchant;
          commit('setProfile', merchant);
          commit('setUserRole', merchant.role?.roles_name || null);
          commit('setStatusKyb', merchant.status_kyb || null);
          commit('setIsAccountDemo', merchant.is_account_demo === true);
          return { success: true, data: merchant };
        }
        return { success: false, error: 'Invalid profile response' };
      } catch (error) {
        console.error('Error fetching user profile:', error);
        return { success: false, error };
      }
    }
  },
  getters: {
    isAuthenticated: (state) => !!state.token,
    userProfile: (state) => state.profile,
    userRole: (state) => state.userRole,
    statusKyb: (state) => state.statusKyb,
    merchantId: (state) => {
      // Get from profile if available, otherwise from localStorage
      if (state.profile && state.profile.id) {
        return state.profile.id;
      }
      const storedId = localStorage.getItem('merchantId');
      return storedId ? parseInt(storedId, 10) : null;
    },
    companyName: (state) => {
      // Get from profile if available, otherwise from localStorage
      if (state.profile && state.profile.company_name) {
        return state.profile.company_name;
      }
      return localStorage.getItem('merchantName') || null;
    },
    isAdmin: (state) => state.userRole === 'Admin',
    isFinance: (state) => state.userRole === 'Finance',
    isStaff: (state) => state.userRole === 'Staff',
    isIT: (state) => state.userRole === 'IT',
    isAccountDemo: (state) => state.isAccountDemo === true,
    canAccessTransfer: (state) => ['Finance', 'Admin'].includes(state.userRole) && state.statusKyb === 'approved',
    isKybApproved: (state) => state.statusKyb === 'approved',
    isPpobEnabled: (state) => {
      // Check if PPOB is enabled from profile
      if (state.profile && state.profile.is_ppob !== undefined) {
        return state.profile.is_ppob === true || state.profile.is_ppob === 1;
      }
      return false;
    },
    isBiUser: (state) => {
      // Check if user is BI user from profile
      if (state.profile && state.profile.is_bi !== undefined) {
        return state.profile.is_bi === true || state.profile.is_bi === 1;
      }
      return false;
    },
    sessionStatus: (state) => ({
      isActive: state.isSessionActive,
      showWarning: state.showTimeoutWarning,
      warningMessage: state.timeoutWarningMessage
    }),
    getRemainingSessionTime: () => {
      return sessionTimeout.getRemainingTime();
    }
  }
};